import tippy from 'tippy.js';
import { BaseAjaxService } from './BaseAjaxService.js';
import { PriceCalculator } from './PriceCalculator.js';
import { NotificationManager } from './NotificationManager.js';

/**
 * Base class for selection management (interior items and furniture)
 */
export class BaseSelectionManager {
  constructor(config) {
    this.config = {
      type: config.type, // 'item' or 'furniture'  
      buttonSelector: config.buttonSelector,
      itemSelector: config.itemSelector,
      containerSelector: config.containerSelector,
      clearButtonSelector: config.clearButtonSelector,
      confirmButtonSelector: config.confirmButtonSelector,
      changeButtonSelector: config.changeButtonSelector,
      frontButtonSelector: config.frontButtonSelector,
      priceAttribute: config.priceAttribute,
      dataAttributes: config.dataAttributes,
      confirmationClass: config.confirmationClass,
      messages: config.messages,
      ...config
    };

    // Global state
    this.userDisabled = ajax_object.userDisabled;
    // Map type to correct ajax_object property
    const confirmedKey = this.config.type === 'item' ? 'interiorConfirmed' : `${this.config.type}Confirmed`;
    this.confirmed = ajax_object[confirmedKey];
    
    // Services
    this.ajaxService = new BaseAjaxService();
    this.priceCalculator = new PriceCalculator();
    this.notificationManager = new NotificationManager();

    // DOM elements
    this.elements = this.initializeElements();
    
    this.init();
  }

  initializeElements() {
    return {
      buttons: document.querySelectorAll(this.config.buttonSelector),
      confirmButton: document.querySelector(this.config.confirmButtonSelector),
      changeButton: document.querySelector(this.config.changeButtonSelector),
      frontButton: document.querySelector(this.config.frontButtonSelector),
      clearButtons: document.querySelectorAll(this.config.clearButtonSelector),
      body: document.body
    };
  }

  init() {
    if (!this.elements.buttons.length) return;

    // Initial setup
    this.fetchAndRenderSelectedItems();
    this.setupUserDisabledTooltips();
    this.setupEventListeners();
    this.setInitialConfirmedState();
    this.initializePrices();
    this.updatePackageValidation();
    this.setupPackageValidationListener();
  }

  setupUserDisabledTooltips() {
    if (this.userDisabled) {
      this.elements.buttons.forEach(button => {
        tippy(button, {
          content: this.config.messages.userDisabled,
          trigger: 'mouseenter focus',
          theme: 'light'
        });
      });
    }
  }

  setupEventListeners() {
    // Selection buttons
    this.elements.buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleItemSelection(button);
      });

      // Add hover functionality for selected buttons
      button.addEventListener('mouseenter', () => {
        this.handleButtonHover(button, true);
      });

      button.addEventListener('mouseleave', () => {
        this.handleButtonHover(button, false);
      });
    });

    // Confirm button
    if (this.elements.confirmButton) {
      this.elements.confirmButton.addEventListener('click', () => {
        this.confirmSelection();
      });
    }

    // Change button
    if (this.elements.changeButton) {
      this.elements.changeButton.addEventListener('click', () => {
        this.resetConfirmation();
      });
    }

    // Clear buttons
    this.elements.clearButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleClearSelection(button);
      });
    });
  }

  setInitialConfirmedState() {
    if (this.confirmed) {
      this.elements.body.classList.add(this.config.confirmationClass);
      this.updateButtonsForConfirmationState(true);
    }
  }

  updateButtonsForConfirmationState(isConfirmed) {
    this.elements.buttons.forEach(button => {
      const parentItem = button.closest(this.config.itemSelector);
      if (!parentItem) return;

      if (isConfirmed) {
        // Disable button and add visual styling
        button.disabled = true;
        button.classList.add('confirmed-disabled');
        parentItem.classList.add('confirmed-disabled');
        
        // Remove hover functionality when confirmed
        button.style.pointerEvents = 'none';
      } else {
        // Re-enable button and remove styling
        button.disabled = false;
        button.classList.remove('confirmed-disabled');
        parentItem.classList.remove('confirmed-disabled');
        
        // Restore hover functionality
        button.style.pointerEvents = 'auto';
      }
    });

    // Also disable/enable clear buttons
    this.elements.clearButtons.forEach(button => {
      if (isConfirmed) {
        button.disabled = true;
        button.classList.add('confirmed-disabled');
      } else {
        button.disabled = false;
        button.classList.remove('confirmed-disabled');
      }
    });
  }

  async handleItemSelection(button) {
    if (this.userDisabled || button.disabled) return;

    const parentItem = button.closest(this.config.itemSelector);
    if (!parentItem) return;

    const ul = parentItem.closest('[data-sub-cat-list]');
    if (!ul) return;

    // Check selection limits
    const allowedNum = parseInt(ul.getAttribute('data-allowed-to-select') || '9999', 10) || 9999;
    const subcatSelected = ul.querySelectorAll(`${this.config.buttonSelector}.selected`);
    const isCurrentlySelected = button.classList.contains('selected');

    if (!isCurrentlySelected && subcatSelected.length >= allowedNum) {
      this.showSelectionLimitTooltip(button, allowedNum);
      return;
    }

    try {
      const itemData = this.extractItemData(button, parentItem);
      const response = await this.ajaxService.saveItemSelection(itemData, this.config.type);
      
      this.updateButtonState(button, response.action_done);
      this.updateSelectedItemsDisplay(itemData, response.action_done);
      this.updatePricing();
      this.updateClearButtonsVisibility();
      this.updateAllowedSelections();
      this.syncValidationState();

    } catch (error) {
      console.error('Failed to save item:', error);
      alert('Failed to save item: ' + error.message);
    }
  }

  extractItemData(button, parentItem) {
    const itemDataJson = button.getAttribute('data-item');
    let itemData;
    
    try {
      itemData = JSON.parse(itemDataJson);
    } catch (e) {
      throw new Error('Invalid item data!');
    }

    // Add plan-specific price if applicable
    if (this.config.type === 'item') {
      const activePlan = document.querySelector('.object-plan-select.active')?.dataset.planTitle.toLowerCase();
      let activePrice;

      if (activePlan) {
        // Regular apartment with plan selection (A/B/C)
        activePrice = parentItem.getAttribute(`data-plan-price-${activePlan}`);
      } else {
        // Resident apartment with single price - use 'hind' as the attribute name
        activePrice = parentItem.getAttribute(`data-plan-price-hind`);
      }

      itemData.price = activePrice;
    }

    // Add category information
    itemData.main_cat = parentItem.getAttribute('data-main-cat') || 'Uncategorized';
    itemData.main_cat_slug = parentItem.getAttribute('data-main-cat-slug') || 'Uncategorized';
    itemData.sub_cat = parentItem.getAttribute('data-sub-cat') || 'Uncategorized';

    return itemData;
  }

  showSelectionLimitTooltip(button, allowedNum) {
    const instance = tippy(button, {
      content: `Lubatud valikute arv antud kategoorias on ${allowedNum}. Eemalda eelnevaid valikuid enne uue tegemist.`,
      trigger: 'manual',
      theme: 'light',
      placement: 'top'
    });

    instance.show();
    setTimeout(() => {
      if (!instance.state.isDestroyed) {
        instance.hide();
        instance.destroy();
      }
    }, 20000);
  }

  updateButtonState(button, actionDone) {
    if (actionDone === 'selected') {
      button.textContent = this.config.messages.selected;
      button.classList.add('selected');
    } else {
      button.textContent = this.config.messages.select;
      button.classList.remove('selected');
    }
  }

  handleButtonHover(button, isHovering) {
    // Only change text if button is selected and not disabled
    if (!button.classList.contains('selected') || this.userDisabled || button.disabled) {
      return;
    }

    if (isHovering) {
      button.textContent = 'Eemalda';
    } else {
      button.textContent = this.config.messages.selected;
    }
  }

  updateSelectedItemsDisplay(itemData, actionDone) {
    const container = document.querySelector(`[data-list-item-with-cat="${itemData.main_cat_slug}"]`);
    if (!container) {
      console.warn(`No container found for category: ${itemData.main_cat}`);
      return;
    }

    const itemsContainer = container.querySelector(this.config.containerSelector);
    if (!itemsContainer) {
      console.warn(`No selected items container found for category: ${itemData.main_cat}`);
      return;
    }

    let subCategoryGroup = itemsContainer.querySelector(`[data-sub-cat-group="${this.sanitizeTitle(itemData.sub_cat)}"]`);

    if (!subCategoryGroup && actionDone === 'selected') {
      subCategoryGroup = this.createSubCategoryGroup(itemData.sub_cat);
      itemsContainer.appendChild(subCategoryGroup);
    }

    if (!subCategoryGroup) return;

    const subCategoryList = subCategoryGroup.querySelector('ul');

    if (actionDone === 'selected') {
      const listItem = document.createElement('li');
      listItem.setAttribute('data-item-name', this.sanitizeTitle(itemData.nimetus));
      if (itemData.interior_id) {
        listItem.setAttribute('data-item-id', itemData.interior_id);
      }
      listItem.setAttribute('data-item-title', itemData.nimetus);
      listItem.textContent = this.getItemDisplayName(itemData);
      subCategoryList.appendChild(listItem);
    } else if (actionDone === 'deselected') {
      let listItem = null;
      if (itemData.interior_id) {
        listItem = subCategoryList.querySelector(`[data-item-id="${itemData.interior_id}"]`);
      }
      if (!listItem) {
        listItem = subCategoryList.querySelector(`[data-item-name="${this.sanitizeTitle(itemData.nimetus)}"]`);
      }
      if (listItem) {
        listItem.remove();
      }
    }

    // Clean up empty subcategory groups
    if (subCategoryList.children.length === 0) {
      subCategoryGroup.remove();
    }

    // Keep left-side selected lists in the exact right-side order for both
    // interior and furniture selection managers.
    if (this.config.type === 'item' || this.config.type === 'furniture') {
      this.sortSelectedItemsByDomOrder(itemsContainer, itemData.main_cat_slug);
    }
  }

  createSubCategoryGroup(subCat) {
    const subCategoryGroup = document.createElement('div');
    subCategoryGroup.classList.add('selected-items-subcategory');
    subCategoryGroup.setAttribute('data-sub-cat-group', this.sanitizeTitle(subCat));
    subCategoryGroup.innerHTML = `<span class="block w-full font-medium mt-6">${subCat}:</span><ul class="text-13"></ul>`;
    return subCategoryGroup;
  }

  sortSelectedItemsByDomOrder(itemsContainer, mainCatSlug) {
    if (!itemsContainer || !mainCatSlug) return;

    const { subCategoryOrder, itemOrder } = this.buildSelectionDomOrder(mainCatSlug);

    const groups = Array.from(itemsContainer.querySelectorAll('[data-sub-cat-group]'));
    groups.sort((a, b) => {
      const aSlug = a.getAttribute('data-sub-cat-group') || '';
      const bSlug = b.getAttribute('data-sub-cat-group') || '';
      const aTitle = this.getSelectedGroupTitle(a);
      const bTitle = this.getSelectedGroupTitle(b);
      const aIndex = this.getSubCategoryOrderIndex(subCategoryOrder, aSlug, aTitle);
      const bIndex = this.getSubCategoryOrderIndex(subCategoryOrder, bSlug, bTitle);

      if (aIndex === bIndex) {
        return aTitle.localeCompare(bTitle);
      }
      return aIndex - bIndex;
    });

    groups.forEach(group => {
      const list = group.querySelector('ul');
      if (!list) return;

      const listItems = Array.from(list.querySelectorAll('li'));
      listItems.sort((a, b) => {
        const aTitle = a.getAttribute('data-item-title') || a.textContent?.trim() || '';
        const bTitle = b.getAttribute('data-item-title') || b.textContent?.trim() || '';
        const aIndex = itemOrder[aTitle] ?? Number.POSITIVE_INFINITY;
        const bIndex = itemOrder[bTitle] ?? Number.POSITIVE_INFINITY;

        if (aIndex === bIndex) {
          return aTitle.localeCompare(bTitle);
        }
        return aIndex - bIndex;
      });

      listItems.forEach(listItem => list.appendChild(listItem));
      itemsContainer.appendChild(group);
    });
  }

  getSelectedGroupTitle(groupElement) {
    const titleElement = groupElement.querySelector('span');
    const rawTitle = titleElement?.textContent?.trim() || '';
    return rawTitle.endsWith(':') ? rawTitle.slice(0, -1).trim() : rawTitle;
  }

  getSubCategoryOrderIndex(subCategoryOrder, slug, title) {
    if (slug in subCategoryOrder) {
      return subCategoryOrder[slug];
    }

    const normalizedTitle = title.toLowerCase();
    if (normalizedTitle in subCategoryOrder) {
      return subCategoryOrder[normalizedTitle];
    }

    return Number.POSITIVE_INFINITY;
  }

  buildSelectionDomOrder(mainCatSlug) {
    const subCategoryOrder = {};
    const itemOrder = {};

    const rightSideCategory = document.querySelector(
      `[data-builder-view][data-list-item-with-cat="${mainCatSlug}"]`
    );
    if (!rightSideCategory) {
      return { subCategoryOrder, itemOrder };
    }

    const subCategoryLists = rightSideCategory.querySelectorAll('[data-sub-cat-list]');
    let itemIndex = 0;

    subCategoryLists.forEach((subCategoryList, subCategoryIndex) => {
      const subCatSlug = subCategoryList.getAttribute('data-sub-cat-list') || '';
      const firstItemInSubCategory = subCategoryList.querySelector(this.config.itemSelector);
      const subCatTitle = firstItemInSubCategory?.getAttribute('data-sub-cat') || '';
      const normalizedSubCatTitle = subCatTitle.toLowerCase();

      if (subCatSlug && subCategoryOrder[subCatSlug] === undefined) {
        subCategoryOrder[subCatSlug] = subCategoryIndex;
      }
      if (normalizedSubCatTitle && subCategoryOrder[normalizedSubCatTitle] === undefined) {
        subCategoryOrder[normalizedSubCatTitle] = subCategoryIndex;
      }

      const itemButtons = subCategoryList.querySelectorAll(`${this.config.itemSelector} ${this.config.buttonSelector}`);
      itemButtons.forEach(button => {
        const itemDataJson = button.getAttribute('data-item');
        if (!itemDataJson) return;

        try {
          const itemData = JSON.parse(itemDataJson);
          const itemTitle = itemData?.nimetus;
          if (!itemTitle) return;

          if (itemOrder[itemTitle] === undefined) {
            itemOrder[itemTitle] = itemIndex++;
          }
        } catch (error) {
          // Ignore malformed data-item JSON
        }
      });
    });

    return { subCategoryOrder, itemOrder };
  }

  updatePricing() {
    if (this.config.type === 'item') {
      const interiorTotal = this.priceCalculator.calculateSelectedItemsPrice(
        this.config.buttonSelector + '.selected',
        this.config.itemSelector,
        this.config.priceAttribute
      );

      // Preserve current extras price when updating interior items
      const extrasTotal = this.priceCalculator.parsePrice(
        this.priceCalculator.elements.totalExtras?.textContent
      ) || 0;

      const totalAddedPrice = interiorTotal + extrasTotal;
      this.priceCalculator.updateAddedPrice(totalAddedPrice);
    }
  }

  updateClearButtonsVisibility() {
    this.elements.clearButtons.forEach(button => {
      const mainCatSlug = button.getAttribute(this.config.dataAttributes.clearCatAttribute);
      if (!mainCatSlug) return;

      const selectedInCat = document.querySelectorAll(
        `${this.config.itemSelector}[data-main-cat-slug="${mainCatSlug}"] ${this.config.buttonSelector}.selected`
      );

      if (selectedInCat.length === 0) {
        button.classList.add('hidden');
      } else {
        button.classList.remove('hidden');
      }
    });
  }

  updateAllowedSelections() {
    const allSubcatLists = document.querySelectorAll('[data-sub-cat-list]');

    allSubcatLists.forEach(ul => {
      const allowedNum = parseInt(ul.getAttribute('data-allowed-to-select') || '9999', 10) || 9999;
      const selectedItems = ul.querySelectorAll(`${this.config.buttonSelector}.selected`);
      const countSelected = selectedItems.length;

      const singleItems = ul.querySelectorAll(this.config.itemSelector);
      singleItems.forEach(item => {
        const selectBtn = item.querySelector(this.config.buttonSelector);
        if (!selectBtn) return;

        const isSelected = selectBtn.classList.contains('selected');

        if (!isSelected && countSelected >= allowedNum) {
          item.classList.add('selection-disabled');
          selectBtn.classList.add('btn-deactive');
        } else {
          item.classList.remove('selection-disabled');
          selectBtn.classList.remove('btn-deactive');
        }
      });
    });
  }

  syncValidationState() {
    this.updatePremiumValidation();
    this.updatePremiumCounts();
    this.updatePackageValidation();
  }

  updatePremiumValidation() {
    // Only apply to interior items with premium validation
    if (this.config.type !== 'item') return;

    const confirmButton = this.elements.confirmButton;
    if (!confirmButton) return;
    if (confirmButton.dataset.forceDisabled === '1') {
      confirmButton.disabled = true;
      return;
    }

    // Check if this was initially marked for premium validation or currently has the class
    const wasPremiumValidationRequired = confirmButton.dataset.originallyPremiumValidation === 'true';
    const currentlyPremiumValidationRequired = confirmButton.classList.contains('premium-validation-required');

    // Set the dataset flag on first run if the class is present
    if (currentlyPremiumValidationRequired && !confirmButton.dataset.originallyPremiumValidation) {
      confirmButton.dataset.originallyPremiumValidation = 'true';
    }

    if (!wasPremiumValidationRequired && !currentlyPremiumValidationRequired) return;

    // Check if all minimum requirements are met
    const allSubcatLists = document.querySelectorAll('[data-sub-cat-list]');
    let allMinimumsAreMet = true;

    allSubcatLists.forEach(ul => {
      const premiumMin = parseInt(ul.getAttribute('data-premium-min') || '0', 10);
      if (premiumMin > 0) {
        const selectedItems = ul.querySelectorAll(`${this.config.buttonSelector}.selected`);
        const countSelected = selectedItems.length;


        if (countSelected < premiumMin) {
          allMinimumsAreMet = false;
        }
      }
    });

    // Update button state
    if (allMinimumsAreMet) {
      confirmButton.classList.remove('premium-validation-required');
      confirmButton.disabled = false;
      this.hidePremiumValidationText();
    } else {
      confirmButton.classList.add('premium-validation-required');
      confirmButton.disabled = true;
      this.showPremiumValidationText();
    }
  }

  async fetchAndRenderSelectedItems() {
    try {
      const response = await this.ajaxService.getSelectedItems(this.config.type);
      const selectedItems = response[`selected_${this.config.type === 'item' ? 'items' : 'furniture'}`] || [];

      selectedItems.forEach(item => {
        this.updateSelectedItemsDisplay(item, 'selected');
        
        // Also update the button state in the DOM
        this.findAndUpdateButtonForItem(item, 'selected');
      });

      this.updatePricing();
      this.updateClearButtonsVisibility();
      this.updateAllowedSelections();
      this.syncValidationState();

    } catch (error) {
      console.error('Failed to fetch selected items:', error);
    }
  }

  findAndUpdateButtonForItem(itemData, actionDone) {
    // Prefer strict matching by name + category context; fallback to name-only for legacy data.
    const allButtons = document.querySelectorAll(this.config.buttonSelector);
    allButtons.forEach(button => {
      const itemDataJson = button.getAttribute('data-item');
      if (itemDataJson) {
        try {
          const buttonItemData = JSON.parse(itemDataJson);
          const sameName = buttonItemData.nimetus === itemData.nimetus;
          const sameInteriorId =
            buttonItemData.interior_id &&
            itemData.interior_id &&
            buttonItemData.interior_id === itemData.interior_id;
          const sameMainCat = (buttonItemData.main_cat_slug || '') === (itemData.main_cat_slug || '');
          const sameSubCat = (buttonItemData.sub_cat || '') === (itemData.sub_cat || '');
          const strictMatch = (sameInteriorId || sameName) && sameMainCat && sameSubCat;
          const legacyMatch = sameName && !itemData.main_cat_slug && !itemData.sub_cat;

          if (strictMatch || legacyMatch) {
            this.updateButtonState(button, actionDone);
          }
        } catch (e) {
          // Ignore invalid JSON
        }
      }
    });
  }

  handleClearSelection(button) {
    const mainCatSlug = button.getAttribute(this.config.dataAttributes.clearCatAttribute);
    const mainCatTitle = button.getAttribute(this.config.dataAttributes.clearCatTitleAttribute);
    
    if (!mainCatSlug) {
      console.warn('No main_cat_slug found on button.');
      return;
    }

    const message = `Kas oled kindel, et soovid kõik valikud kategoorias "${mainCatTitle}" tühistada?`;
    
    this.notificationManager.showResetConfirmation(
      message,
      () => this.clearCategoryChoices(mainCatSlug)
    );
  }

  async clearCategoryChoices(mainCatSlug) {
    try {
      await this.ajaxService.clearSelectedItems(mainCatSlug, this.config.type);

      // Clear from DOM
      const container = document.querySelector(`[data-list-item-with-cat="${mainCatSlug}"]`);
      if (container) {
        const itemsContainer = container.querySelector(this.config.containerSelector);
        if (itemsContainer) {
          itemsContainer.innerHTML = '';
        }
      }

      // Remove selected state from buttons
      const allItems = document.querySelectorAll(this.config.itemSelector);
      allItems.forEach(itemEl => {
        const btn = itemEl.querySelector(this.config.buttonSelector);
        if (!btn) return;

        const itemDataJson = btn.getAttribute('data-item');
        if (!itemDataJson) return;

        try {
          const itemObj = JSON.parse(itemDataJson);
          if (itemObj.main_cat_slug === mainCatSlug) {
            this.updateButtonState(btn, 'deselected');
          }
        } catch (err) {
          console.error('Failed to parse itemObj:', err);
        }
      });

      this.updatePricing();
      this.updateClearButtonsVisibility();
      this.updateAllowedSelections();
      this.syncValidationState();

    } catch (error) {
      console.error('Error clearing selected items:', error);
      alert('Viga! Palun proovi uuesti.');
    }
  }

  async confirmSelection() {
    try {
      const timestamp = new Date().toLocaleString();
      await this.ajaxService.confirmSelection(this.config.type, timestamp);

      this.elements.confirmButton.classList.add('confirmed');
      this.elements.confirmButton.textContent = this.config.messages.confirmed;
      
      if (this.elements.frontButton) {
        this.elements.frontButton.textContent = this.config.messages.confirmed;
      }
      
      this.elements.body.classList.add(this.config.confirmationClass);

      // Disable all selection buttons when confirmed
      this.updateButtonsForConfirmationState(true);

      // Special handling for interior confirmation
      if (this.config.type === 'item') {
        const plansBtn = document.querySelector("[data-trigger-builder-view='plan']");
        if (plansBtn) plansBtn.click();
      }

      const messageType = this.config.type === 'item' ? 'interior' : this.config.type;
      this.notificationManager.showSuccessMessage('confirmed', messageType, timestamp);

    } catch (error) {
      console.error('Error confirming selection:', error);
    }
  }

  async resetConfirmation() {
    try {
      await this.ajaxService.resetConfirmation(this.config.type);

      this.elements.confirmButton.classList.remove('confirmed');
      this.elements.confirmButton.textContent = this.config.messages.confirm;
      
      if (this.elements.frontButton) {
        this.elements.frontButton.textContent = this.config.messages.frontSelect;
      }
      
      this.elements.body.classList.remove(this.config.confirmationClass);

      // Re-enable all selection buttons when confirmation is reset
      this.updateButtonsForConfirmationState(false);

      const messageType = this.config.type === 'item' ? 'interior' : this.config.type;
      this.notificationManager.showSuccessMessage('reset', messageType);

    } catch (error) {
      console.error('Error resetting confirmation:', error);
    }
  }


  /**
   * Initialize prices for items, handling both regular and resident apartments
   */
  initializePrices() {
    if (this.config.type !== 'item') return;

    // Check if this is a resident apartment (no plan selection buttons)
    const hasPlans = document.querySelector('.object-plan-select');

    if (!hasPlans) {
      // Resident apartment - initialize prices using single 'hind' attribute
      this.priceCalculator.updatePricesForPlan(null);
    }
    // For regular apartments with plans, prices are updated when plan is selected
  }

  sanitizeTitle(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  getItemDisplayName(itemData) {
    if (!itemData || typeof itemData !== 'object') {
      return '';
    }
    return itemData.display_nimetus || itemData.nimetus || '';
  }

  showPremiumValidationText() {
    const textElement = document.querySelector('[data-premium-validation-required-text]');
    if (textElement) {
      textElement.classList.remove('hidden');
    }
  }

  hidePremiumValidationText() {
    const textElement = document.querySelector('[data-premium-validation-required-text]');
    if (textElement) {
      textElement.classList.add('hidden');
    }
  }

  updatePackageValidation() {
    // Only apply to interior items
    if (this.config.type !== 'item') return;

    const confirmButton = this.elements.confirmButton;
    if (!confirmButton) return;
    if (confirmButton.dataset.forceDisabled === '1') {
      confirmButton.disabled = true;
      return;
    }

    // Check if this is a Mugav resident by looking for the validation text element
    const packageValidationText = document.querySelector('[data-package-validation-required-text]');
    if (!packageValidationText) return; // Not a Mugav resident

    // Check if a package is selected by looking for the meta field via AJAX
    this.checkPackageSelection().then(hasPackage => {
      if (hasPackage) {
        confirmButton.classList.remove('package-validation-required');
        confirmButton.disabled = false;
        this.hidePackageValidationText();
      } else {
        confirmButton.classList.add('package-validation-required');
        confirmButton.disabled = true;
        this.showPackageValidationText();
      }
    });
  }

  async checkPackageSelection() {
    try {
      const response = await this.ajaxService.request('check_package_selection');
      return response && response.has_package;
    } catch (error) {
      return false;
    }
  }

  showPackageValidationText() {
    const textElement = document.querySelector('[data-package-validation-required-text]');
    if (textElement) {
      textElement.classList.remove('hidden');
    }
  }

  hidePackageValidationText() {
    const textElement = document.querySelector('[data-package-validation-required-text]');
    if (textElement) {
      textElement.classList.add('hidden');
    }
  }

  setupPackageValidationListener() {
    // Listen for package validation update events from packageSelection.js
    document.addEventListener('packageValidationUpdate', () => {
      this.updatePackageValidation();
    });
  }

  updatePremiumCounts() {
    // Only apply to interior items
    if (this.config.type !== 'item') return;

    // Only for Premium/Personaalne residents
    const premiumCountElements = document.querySelectorAll('[data-premium-validation-counts]');
    if (premiumCountElements.length === 0) return;

    // Group subcategories by main category
    const categoryData = {};
    const allSubcatLists = document.querySelectorAll('[data-sub-cat-list]');

    allSubcatLists.forEach(ul => {
      const premiumMin = parseInt(ul.getAttribute('data-premium-min') || '0', 10);

      // Find the main category for this subcategory
      const categoryContainer = ul.closest('[data-list-item-with-cat]');
      if (!categoryContainer) return;

      const mainCatSlug = categoryContainer.getAttribute('data-list-item-with-cat');
      if (!mainCatSlug) return;

      if (!categoryData[mainCatSlug]) {
        categoryData[mainCatSlug] = {
          totalRequired: 0,
          selectedCount: 0
        };
      }

      // Add to total required count
      categoryData[mainCatSlug].totalRequired += premiumMin;

      // Count selected items in this subcategory, but only up to the required minimum
      const selectedItems = ul.querySelectorAll(`${this.config.buttonSelector}.selected`);
      const selectedCount = selectedItems.length;
      const countTowardsRequired = Math.min(selectedCount, premiumMin);
      categoryData[mainCatSlug].selectedCount += countTowardsRequired;
    });

    // Update each premium count display
    premiumCountElements.forEach(countElement => {
      const categoryContainer = countElement.closest('[data-list-item-with-cat]');
      if (!categoryContainer) return;

      const mainCatSlug = categoryContainer.getAttribute('data-list-item-with-cat');
      const data = categoryData[mainCatSlug];

      if (data && data.totalRequired > 0) {
        // Update separate total and selected spans
        const totalSpan = countElement.querySelector('[data-premium-validation-counts-total]');
        const selectedSpan = countElement.querySelector('[data-premium-validation-counts-selected]');

        if (totalSpan) totalSpan.textContent = data.totalRequired;
        if (selectedSpan) selectedSpan.textContent = data.selectedCount;

        countElement.style.display = 'inline-block';
      } else {
        countElement.style.display = 'none';
      }
    });
  }

}
